import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useContext } from 'react';
import { Anchor, GridColumn, Modal, ModalProvider, ModalTrigger, Table, TableCell, Text } from 'telia-front-react';
import GaDataStore from '../stores/GaDataStore';
import LanguageStore from '../stores/LanguageStore';
import StoresStore, { Store } from '../stores/StoresStore';
import { BusinessOpeningHours, SpecialOpeningHours } from './OpeningHours';

interface BaseProps {
  language: string;
  translate: (key: string) => string;
}

interface TownStoresOpeningInformationProps extends BaseProps {
  cityName: string;
  stores: Store[];
}

interface TableBodyProps extends BaseProps {
  stores: Store[];
}

interface StoresOpeningInformationProps {
  storesStore?: StoresStore;
  languageStore?: LanguageStore;
}

const StoresOpeningInformation = (props: StoresOpeningInformationProps): JSX.Element => {
  const { translate } = useContext(TranslationsContext);
  const {
    storesStore: { storesByCity, fetchStores, cityNamesList },
    languageStore: { currentLanguage }
  } = props;

  useEffect(() => {
    fetchStores();
  }, []);

  return (
    <>
      {[].concat(
        ...cityNamesList.map((cityNames, index) => (
          <GridColumn key={index} width={['xs-3']}>
            <Text>
              {cityNames.map((cityName) => (
                <TownStoresOpeningInformationModal
                  key={cityName}
                  cityName={cityName}
                  stores={storesByCity.get(cityName)}
                  language={currentLanguage}
                  translate={translate}
                />
              ))}
            </Text>
          </GridColumn>
        ))
      )}
    </>
  );
};

const TownStoresOpeningInformationModal = (props: TownStoresOpeningInformationProps): JSX.Element => {
  const { cityName, translate, stores, language } = props;
  const cityTranslation = translate(`stores.city.${cityName.toLowerCase()}`);
  return (
    <p>
      <ModalProvider>
        <ModalTrigger>
          <Anchor
            url="#"
            onClick={() =>
              GaDataStore.pushWithoutCmsData({
                event: 'AutoEvent',
                eventCategory: 'Kontaktid',
                eventAction: 'Modal',
                eventLabel: `Link - ${cityName}`
              })
            }
          >
            {cityTranslation}
          </Anchor>
        </ModalTrigger>
        <Modal>
          <Text>
            <h1>{cityTranslation}</h1>
            <Table>
              <TableHeading translate={translate} />
              <TableBody stores={stores} language={language} translate={translate} />
            </Table>
          </Text>
        </Modal>
      </ModalProvider>
    </p>
  );
};

const TableHeading = ({ translate }: { translate: (key: string) => string }): JSX.Element => {
  return (
    <thead>
      <tr>
        <TableCell type="th" width={4}>
          {translate('stores.locations')}
        </TableCell>
        <TableCell type="th" width={4} className="text-center">
          {translate('stores.address')}
        </TableCell>
        <TableCell type="th" width={4} className="text-right">
          {translate('stores.openingHours')}
        </TableCell>
      </tr>
    </thead>
  );
};

const TableBody = (props: TableBodyProps): JSX.Element => {
  const { translate, stores, language } = props;
  return (
    <tbody>
      {stores.map((store) => (
        <tr key={store.name}>
          <TableCell type="td">{translate(`store.name.${store.name.split(' ').join('_')}`)}</TableCell>
          <TableCell type="td" className="text-center">
            {store.googleMapsLink ? (
              <Anchor url={store.googleMapsLink} target="_blank">
                {store.streetAddress[language]}
              </Anchor>
            ) : (
              store.streetAddress[language]
            )}
          </TableCell>
          <TableCell type="td" className="text-right">
            <BusinessOpeningHours hours={store.businessHours} translate={translate} language={language} />
            <SpecialOpeningHours hours={store.specialHours} translate={translate} language={language} />
            {store.locationDescription[language] && <div>{store.locationDescription[language]}</div>}
            {store.additionalDescription[language] && <div dangerouslySetInnerHTML={{ __html: store.additionalDescription[language] }} />}
          </TableCell>
        </tr>
      ))}
    </tbody>
  );
};

export default inject('storesStore', 'languageStore')(observer(StoresOpeningInformation));
